import {
  faClipboard,
  faPlus,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import FilterTab, { FilterTabItem } from 'components/common/FilterTab';
import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import StudentsTable, { studentsTablecolumns } from './table/StudentsTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyFindCandidateQuery } from 'service/institutionQuery/institutionQuery';
import { User } from './table/studentTable.type';
import { useSelector } from 'react-redux';
import { RootState } from 'store/rootReducer';
import useAdvanceServerTable from 'hooks/useAdvanceServerTable';
import { CandidateResponse, Params } from './institutionStudent.type';
import CanAccess from 'components/modules/auth/Access/CanAccess';

const defaultBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'Students',
    url: '/hub/students',
    active: true
  }
];

const pageSize = 10;

const InstitutionStudents = () => {
  const [search, setSearch] = useState('');

  const [findCandidate, { data: candidateResponse, isFetching }] =
    useLazyFindCandidateQuery();
  const user = useSelector((state: RootState) => state.user.user);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const page = Number(searchParams.get('page')) || 1;
  const isVerified = searchParams.get('isVerified') || undefined;
  const searchKey = searchParams.get('search') || undefined;
  const candidates = candidateResponse as CandidateResponse;
  const data = candidates?.data || [];
  const totalRows = candidates?.pagination?.total || 0;
  const unVerifiedTotal = candidates?.pagination?.unVerifiedTotal || 0;
  const overAll = candidates?.pagination?.overAll || 0;

  const table = useAdvanceServerTable({
    data: data as User[],
    columns: studentsTablecolumns,
    pageSize,
    pagination: true,
    selection: true,
    totalRows: totalRows,
    page,
    isLoading: isFetching
  });

  const handleTabChange = (value: string) => {
    if (value === 'pending-verification') {
      navigate('?isVerified=false');
    } else {
      navigate('#');
    }
  };

  const tabItems: FilterTabItem[] = useMemo(() => {
    return [
      {
        label: 'All',
        value: 'all',
        count: overAll,
        onClick: handleTabChange
      },
      {
        label: 'Pending Verification',
        value: 'pending-verification',
        count: unVerifiedTotal,
        onClick: handleTabChange
      }
    ];
  }, [overAll, unVerifiedTotal]);

  const handleShare = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/register/institution-student/${user._id}`
    );
    alert('Copied !');
  };

  const handleSearch = () => {
    if (search) {
      searchParams.set('search', search);
    } else {
      searchParams.delete('search');
    }
    navigate(`?${searchParams.toString()}`);
  };

  useEffect(() => {
    const params: Params = {
      page,
      limit: pageSize
    };
    if (isVerified) params.isVerified = isVerified;
    if (searchKey) params.search = searchKey;
    findCandidate(params);
  }, [pageSize, page, isVerified, searchKey]);

  return (
    <div>
      <PageBreadcrumb items={defaultBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-4">Students</h2>
        <FilterTab
          tabItems={tabItems}
          className="mb-2"
          active={isVerified ? 'pending-verification' : undefined}
        />
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap justify-content-between gap-3">
              <SearchBox
                placeholder="Search"
                onChange={e => setSearch(e.target.value)}
                onEnter={handleSearch}
                value={search}
              />
              <CanAccess action="canWrite" entity="student">
                <div className="ms-xxl-auto">
                  <Button
                    variant="link"
                    className="text-body me-4 px-0"
                    title="Copy link"
                    onClick={handleShare}
                  >
                    <FontAwesomeIcon icon={faClipboard} className="fs-9 me-2" />
                    Share Link
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="mx-2"
                    as={Link}
                    to="/hub/students/bulk"
                  >
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    Bulk upload
                  </Button>
                  <Button variant="primary" as={Link} to="/hub/students/add">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add student
                  </Button>
                </div>
              </CanAccess>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <StudentsTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default InstitutionStudents;
